import React, { useState } from 'react';
import { KfsCalculatedValues } from '../types/kfs.types';
import { formatINR, formatAPR } from '../utils/kfsFormulas';
import IconSystem from 'icon-collection/icon-systems';

interface KfsSummaryPanelProps {
  values: KfsCalculatedValues | null;
}

interface SummaryRow {
  fieldNo: number;
  label: string;
  getValue: (v: KfsCalculatedValues) => string;
  tooltip: string;
  isHighlight?: boolean;
}

const SUMMARY_ROWS: SummaryRow[] = [
  {
    fieldNo: 6,
    label: 'Insurance Charge',
    getValue: (v) => formatINR(v.insuranceCharge),
    tooltip: 'Gross Weight × ₹1 per gram, rounded to nearest rupee',
  },
  {
    fieldNo: 7,
    label: 'APR',
    getValue: (v) => formatAPR(v.apr),
    tooltip: 'Interest Rate + (Fee Charges ÷ Loan Amount)',
  },
  {
    fieldNo: 11,
    label: 'Total Interest to be Paid',
    getValue: (v) => formatINR(v.totalInterest),
    tooltip: 'Loan Amount × Interest Rate',
  },
  {
    fieldNo: 12,
    label: 'Fee Charges',
    getValue: (v) => formatINR(v.feeCharges),
    tooltip: '(Insurance Charge × 12 months) + ₹59 processing fee, rounded',
  },
  {
    fieldNo: 13,
    label: 'Payable to RE',
    getValue: (v) => formatINR(v.payableToRE),
    tooltip: 'Same as Fee Charges',
  },
  {
    fieldNo: 14,
    label: 'Net Disbursement',
    getValue: (v) => formatINR(v.netDisbursement),
    tooltip: 'Loan Amount − ₹59 processing fee',
  },
  {
    fieldNo: 15,
    label: 'Total Amount to be Paid by Borrower',
    getValue: (v) => formatINR(v.totalAmountPayable),
    tooltip: 'Loan Amount + Total Interest + Fee Charges',
  },
  {
    fieldNo: 16,
    label: 'APR',
    getValue: (v) => formatAPR(v.apr),
    tooltip: 'Interest Rate + (Fee Charges ÷ Loan Amount)',
  },
];

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);

  return (
    <span
      className="kfs-tooltip-wrapper"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="kfs-info-icon">i</span>
      {show && <span className="kfs-tooltip-text">{text}</span>}
    </span>
  );
};

const KfsSummaryPanel: React.FC<KfsSummaryPanelProps> = ({ values }) => {
  return (
    <div className="kfs-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="kfs-card-header">
        <div className="kfs-card-header-icon green">
          <IconSystem.chart size={16} />
        </div>
        <h3>KFS Summary</h3>
        <span>{values ? 'Calculated' : 'Awaiting inputs'}</span>
      </div>
      <div className="kfs-card-body" style={{ flex: 1, padding: 0 }}>
        <table className="kfs-summary-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th>Particulars</th>
              <th style={{ width: 32 }}></th>
              <th style={{ textAlign: 'right' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {SUMMARY_ROWS.map((row) => (
              <tr key={`${row.fieldNo}-${row.label}`} className={row.fieldNo === 15 ? 'kfs-highlight-row' : ''}>
                <td className="kfs-field-no">{row.fieldNo}</td>
                <td>{row.label}</td>
                <td className="kfs-tooltip-cell">
                  <InfoTooltip text={row.tooltip} />
                </td>
                <td className={`kfs-value ${values ? (row.fieldNo === 15 ? 'highlight' : '') : 'placeholder'}`}>
                  {values ? row.getValue(values) : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KfsSummaryPanel;
